import { Button, Checkbox, Select, Tag, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StaffApplicantSearchContainerApplicantApplicantUserResultsFieldsFragment, StaffApplicantSearchFilterContainerEntityFieldsFragment } from '../../../../generated';
import FilterOptionLabel from '../../../shared/filter-option-label';
import { ReloadOutlined } from '@ant-design/icons';

const { Option } = Select;

const ApplicantSearchParamKey = {
  updatedAt: 'updatedAt',
  createdAt: 'createdAt',
  dateOfBirth: 'dateOfBirth',
  gender: 'gender',
  generationalSuffix: 'generationalSuffix',
  schemaVersion: 'schemaVersion',
  applyingTo: 'applyingTo',
  tags: 'tags',
  accessBlocked: 'accessBlocked',
  accessBlockedYesChecked: 'accessBlockedYesChecked',
  accessBlockedNoChecked: 'accessBlockedNoChecked',
  bothAccessBlocked: 'bothAccessBlocked'
};

const transformCount = (input: any) => {
  return Object.keys(input)
    .filter((key) => key !== '__typename')
    .reduce((acc: any, key) => {
      const values = input[key]?.reduce((subAcc: any, detail: any) => {
        subAcc[detail.value] = detail.count;
        return subAcc;
      }, {});
      acc[key] = values;
      return acc;
    }, {});
};

const updatesFiltersOptions = [
  { idSuffix: 'today', label: 'Today' },
  { idSuffix: 'yesterday', label: 'Yesterday' },
  { idSuffix: 'lastWeek', label: 'Within 7 days' },
  { idSuffix: 'lastMonth', label: 'Within 30 days' },
  { idSuffix: 'lastYear', label: 'Within 365 days' }
];

const updatesFilters = [
  {
    label: 'Last Updated',
    category: ApplicantSearchParamKey.updatedAt,
    items: updatesFiltersOptions
  },
  {
    label: 'Created At',
    category: ApplicantSearchParamKey.createdAt,
    items: updatesFiltersOptions
  }
];

const dateOfBirthList = ['65+', '55-65', '35-45', '25-35', '18-25'];

const genderList = [
  {
    label: 'Male',
    value: 'M'
  },
  {
    label: 'Female',
    value: 'F'
  },
  {
    label: 'Other',
    value: 'X'
  }
];

const generationalSuffixList = [
  {
    label: 'Junior',
    value: 'Jr'
  },
  {
    label: 'Senior',
    value: 'Sr'
  }
];

const schemaVersionFilter = [{ value: '1.0.0', label: '1.0.0' }];

interface ApplicantSearchFilterProps {
  data: StaffApplicantSearchContainerApplicantApplicantUserResultsFieldsFragment;
  entitiesList: StaffApplicantSearchFilterContainerEntityFieldsFragment[];
  allTags: string[];
  refreshTagHandler: () => void;
}

interface ApplicantSearchFilterInterface {
  updatedAt: string[];
  createdAt: string[];
  dateOfBirth: string[];
  gender: string[];
  generationalSuffix: string[];
  schemaVersion: string[];
  applyingTo: string[];
  tags: string[];
  accessBlocked: boolean | null;
  accessBlockedYesChecked: boolean;
  accessBlockedNoChecked: boolean;
}

export const ApplicantSearchFilter: React.FC<ApplicantSearchFilterProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const count = transformCount(props?.data);

  const [checkedFilter, setCheckedFilter] = useState<ApplicantSearchFilterInterface>({
    updatedAt: searchParams.get(ApplicantSearchParamKey.updatedAt)?.split(',') || [],
    createdAt: searchParams.get(ApplicantSearchParamKey.createdAt)?.split(',') || [],
    dateOfBirth: searchParams.get(ApplicantSearchParamKey.dateOfBirth)?.split(',') || [],
    gender: searchParams.get(ApplicantSearchParamKey.gender)?.split(',') || [],
    generationalSuffix: searchParams.get(ApplicantSearchParamKey.generationalSuffix)?.split(',') || [],
    schemaVersion: searchParams.get(ApplicantSearchParamKey.schemaVersion)?.split(',') || [],
    applyingTo: searchParams.get(ApplicantSearchParamKey.applyingTo)?.split(',') || [],
    tags: searchParams.get(ApplicantSearchParamKey.tags)?.split(',') || [],
    accessBlocked:
      searchParams.get(ApplicantSearchParamKey.accessBlocked) === 'true' ? true : searchParams.get(ApplicantSearchParamKey.accessBlocked) === 'false' ? false : null,
    accessBlockedYesChecked: searchParams.get(ApplicantSearchParamKey.accessBlocked) === 'true',
    accessBlockedNoChecked: searchParams.get(ApplicantSearchParamKey.accessBlocked) === 'false'
  });

  const hasInitialTagsSet = useRef(false);

  const handleFilterCheck = (event: any, filterCategory: keyof typeof checkedFilter) => {
    searchParams.set('page', '1');
    const { id: filterId, checked } = event.target;
    if (!checked) {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',').filter((item) => item !== filterId);
        if (updatedSearchCategory.length > 0) {
          searchParams.set(filterCategory, updatedSearchCategory.join(','));
        } else {
          searchParams.delete(filterCategory);
        }
        setSearchParams(searchParams);
      }
    } else {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',');
        updatedSearchCategory.push(filterId);
        searchParams.set(filterCategory, updatedSearchCategory.join(','));
        setSearchParams(searchParams);
      } else {
        searchParams.set(filterCategory, filterId);
        setSearchParams(searchParams);
      }
    }

    const filterName = filterId;
    setCheckedFilter((prevState) => {
      const currentCategory = prevState[filterCategory];

      if (Array.isArray(currentCategory)) {
        const updatedCategory = checked ? [...currentCategory, filterName] : currentCategory.filter((item: any) => item !== filterName);

        return {
          ...prevState,
          [filterCategory]: updatedCategory
        };
      }
      return prevState;
    });
  };

  const isFilterSelected = (idSuffix: string, category: string) => {
    return searchParams.get(category)?.split(',').includes(idSuffix);
  };

  const handleAccessBlockedChange = (isChecked: boolean, isYesOption: boolean) => {
    searchParams.set('page', '1');
    setCheckedFilter((prevState) => {
      let newAccessBlockedState: boolean | null;
      let newYesChecked = prevState.accessBlockedYesChecked;
      let newNoChecked = prevState.accessBlockedNoChecked;
      if (isYesOption) {
        newYesChecked = isChecked;
      } else {
        newNoChecked = isChecked;
      }
      if (newYesChecked && newNoChecked) {
        newAccessBlockedState = null;
        searchParams.set(ApplicantSearchParamKey.bothAccessBlocked, 'true');
      } else if (newYesChecked) {
        newAccessBlockedState = true;
        searchParams.delete(ApplicantSearchParamKey.bothAccessBlocked);
        newNoChecked = false;
      } else if (newNoChecked) {
        newAccessBlockedState = false;
        searchParams.delete(ApplicantSearchParamKey.bothAccessBlocked);
        newYesChecked = false;
      } else {
        newAccessBlockedState = null;
        searchParams.delete(ApplicantSearchParamKey.bothAccessBlocked);
      }
      if (newAccessBlockedState === null) {
        searchParams.delete(ApplicantSearchParamKey.accessBlocked);
      } else {
        searchParams.set(ApplicantSearchParamKey.accessBlocked, String(newAccessBlockedState));
      }
      setSearchParams(searchParams);
      return {
        ...prevState,
        accessBlocked: newAccessBlockedState,
        accessBlockedYesChecked: newYesChecked,
        accessBlockedNoChecked: newNoChecked
      };
    });
  };

  const clearAllFilter = () => {
    const filterKeys = Object.keys(ApplicantSearchParamKey) as (keyof ApplicantSearchFilterInterface)[];

    const hasFilters = filterKeys.some((key) => searchParams.get(key));

    if (!hasFilters) {
      return;
    }

    searchParams.delete('bothAccessBlocked');

    filterKeys.forEach((key) => {
      searchParams.delete(key);
    });

    setSearchParams(searchParams);
    setCheckedFilter((prevState) => ({
      ...prevState,
      updatedAt: [],
      createdAt: [],
      dateOfBirth: [],
      gender: [],
      generationalSuffix: [],
      schemaVersion: [],
      applyingTo: [],
      role: [],
      tags: [],
      accessBlocked: null,
      accessBlockedYesChecked: false,
      accessBlockedNoChecked: false
    }));
  };

  useEffect(() => {
    if (searchParams.get(ApplicantSearchParamKey.bothAccessBlocked) === 'true') {
      setCheckedFilter((prevState) => ({
        ...prevState,
        accessBlockedYesChecked: true,
        accessBlockedNoChecked: true
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (props.data && count.tags && !hasInitialTagsSet.current) {
      hasInitialTagsSet.current = true;
    }
  }, [count.tags, props.data]);

  const openFilter = (filterCategory: keyof typeof checkedFilter) => {
    const filterValue = checkedFilter[filterCategory];
    if (filterCategory === ApplicantSearchParamKey.accessBlocked) {
      if (
        (checkedFilter.accessBlockedYesChecked || searchParams.get(ApplicantSearchParamKey.bothAccessBlocked) == 'true') &&
        (checkedFilter.accessBlockedNoChecked || searchParams.get(ApplicantSearchParamKey.bothAccessBlocked) == 'true')
      ) {
        return true;
      }
      return checkedFilter.accessBlockedYesChecked || checkedFilter.accessBlockedNoChecked;
    } else {
      return Array.isArray(filterValue) && filterValue.length > 0;
    }
  };

  const handleSelectChange = (paramName: keyof typeof checkedFilter, value: string[]) => {
    searchParams.set('page', '1');
    const paramValue = value.join(',');
    console.log('paramNameparamValue', paramName, 'paramValue', paramValue);
    paramValue ? searchParams.set(paramName, paramValue) : searchParams.delete(paramName);
    setSearchParams(searchParams);
    setCheckedFilter((prevState) => ({
      ...prevState,
      [paramName]: value
    }));
  };

  return (
    <div className="flex-[1_1_30%] mr-[10px] min-w-[220px] max-w-[300px] max-h-[200vh] overflow-x-hidden">
      <div style={{ marginBottom: '15px' }}>
        <Button onClick={clearAllFilter} type="primary">
          Clear All Filters
        </Button>
      </div>
      <div className="overflow-x-hidden overflow-y-auto max-h-[750px] scrollbar-hide">
        <div className="flex flex-wrap w-[200px] mb-2">
          {checkedFilter?.tags.map((tag) => (
            <Tag
              className="mb-2"
              key={tag}
              closable
              onClose={() => {
                handleFilterCheck({ target: { checked: false, id: tag } }, 'tags');
              }}
            >
              {tag}
            </Tag>
          ))}
        </div>
        <FilterOptionLabel label="State" initiallyOpen={openFilter(ApplicantSearchParamKey.accessBlocked as keyof ApplicantSearchFilterInterface)}>
          <Checkbox
            id="accessBlockedYes"
            checked={checkedFilter.accessBlockedYesChecked || searchParams.get(ApplicantSearchParamKey.bothAccessBlocked) == 'true'}
            onChange={(event: any) => handleAccessBlockedChange(event.target.checked, true)}
          >
            <p className="checkBoxLabel">Access Restricted ({count[ApplicantSearchParamKey.accessBlocked]?.true ?? 0})</p>
          </Checkbox>

          <Checkbox
            id="accessBlockedNo"
            checked={checkedFilter.accessBlockedNoChecked || searchParams.get(ApplicantSearchParamKey.bothAccessBlocked) == 'true'}
            onChange={(event: any) => handleAccessBlockedChange(event.target.checked, false)}
          >
            <p className="checkBoxLabel">Not Restricted ({count[ApplicantSearchParamKey.accessBlocked]?.false ?? 0})</p>
          </Checkbox>
        </FilterOptionLabel>

        <FilterOptionLabel label="Age Group" initiallyOpen={openFilter(ApplicantSearchParamKey.dateOfBirth as keyof ApplicantSearchFilterInterface)}>
          {dateOfBirthList.map((option) => (
            <Checkbox
              key={option}
              id={option}
              checked={isFilterSelected(option, ApplicantSearchParamKey.dateOfBirth)}
              onChange={(event: any) => {
                handleFilterCheck(event, ApplicantSearchParamKey.dateOfBirth as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option} ({count[ApplicantSearchParamKey.dateOfBirth]?.[option] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>

        <FilterOptionLabel label="Gender" initiallyOpen={openFilter(ApplicantSearchParamKey.gender as keyof ApplicantSearchFilterInterface)}>
          {genderList.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, ApplicantSearchParamKey.gender)}
              onChange={(event: any) => {
                handleFilterCheck(event, ApplicantSearchParamKey.gender as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count[ApplicantSearchParamKey.gender]?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>

        <FilterOptionLabel label="Generational Suffix" initiallyOpen={openFilter(ApplicantSearchParamKey.generationalSuffix as keyof ApplicantSearchFilterInterface)}>
          {generationalSuffixList.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, ApplicantSearchParamKey.generationalSuffix)}
              onChange={(event: any) => {
                handleFilterCheck(event, ApplicantSearchParamKey.generationalSuffix as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count[ApplicantSearchParamKey.generationalSuffix]?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>

        <FilterOptionLabel label="Applying To" initiallyOpen={openFilter(ApplicantSearchParamKey.applyingTo as keyof ApplicantSearchFilterInterface)}>
          <Select
            allowClear
            mode="multiple"
            placeholder="Select Applying To"
            className="w-52 my-2"
            value={checkedFilter.applyingTo}
            onChange={(value) => {
              searchParams.set('page', '1');
              setCheckedFilter((prevState) => ({
                ...prevState,
                applyingTo: value
              }));
              const applyingTo = value.join(',');
              if (applyingTo) {
                searchParams.set(ApplicantSearchParamKey.applyingTo, applyingTo);
              } else {
                searchParams.delete(ApplicantSearchParamKey.applyingTo);
              }
              setSearchParams(searchParams);
            }}
          >
            {props.entitiesList.filter((entity: StaffApplicantSearchFilterContainerEntityFieldsFragment) => entity.isClient === true).map((entity: StaffApplicantSearchFilterContainerEntityFieldsFragment) => (
              <Option key={entity.id} value={entity.id}>
                <Tooltip placement="right" title={`${entity.entityName} (${count[ApplicantSearchParamKey.applyingTo]?.[entity.id] ?? 0})`}>
                  {entity.entityName} ({count[ApplicantSearchParamKey.applyingTo]?.[entity.id] ?? 0})
                </Tooltip>
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        <FilterOptionLabel label="Tag" initiallyOpen={openFilter('tags')} icon={<ReloadOutlined />} iconHandler={props?.refreshTagHandler} iconToolTipText="Refresh Tags">
          <Select
            mode="multiple"
            style={{ width: '200px' }}
            allowClear
            placeholder="Select Tag"
            value={checkedFilter.tags}
            onChange={(value) => handleSelectChange('tags', value)}
          >
            {props?.allTags?.map((tag) => (
              <Option key={tag} value={tag}>
                <Tooltip placement="right" title={`${tag} (${count['tags']?.[tag] ?? 0})`}>
                  {tag}({count['tags']?.[tag] ?? 0})
                </Tooltip>
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        {updatesFilters.map((filter) => (
          <FilterOptionLabel key={filter.category} label={filter.label} initiallyOpen={openFilter(filter.category as keyof ApplicantSearchFilterInterface)}>
            {updatesFiltersOptions.map((option) => (
              <Checkbox
                key={option.idSuffix}
                id={option.idSuffix}
                checked={isFilterSelected(option.idSuffix, filter.category)}
                onChange={(event: any) => {
                  handleFilterCheck(event, filter.category as keyof typeof checkedFilter);
                }}
              >
                <p className="checkBoxLabel">
                  {option.label} ({count[filter.category]?.[option.idSuffix] ?? 0})
                </p>
              </Checkbox>
            ))}
          </FilterOptionLabel>
        ))}

        <FilterOptionLabel label="Schema Version" initiallyOpen={openFilter(ApplicantSearchParamKey.schemaVersion as keyof ApplicantSearchFilterInterface)}>
          {schemaVersionFilter.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, ApplicantSearchParamKey.schemaVersion)}
              onChange={(event: any) => {
                handleFilterCheck(event, ApplicantSearchParamKey.schemaVersion as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count[ApplicantSearchParamKey.schemaVersion]?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>
      </div>
    </div>
  );
};
